import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import withModal from '../../global/modal/withModal';
import { UNIFORM_TAG_FORM_MODAL } from '../../../constants/modals';
import { getAsset } from '../../../util/assets';
import Select from '../../global/Select';
import Button from '../../global/Button';
import InputGroup from '../../global/InputGroup';
import ShirtIcon from '../../icons/ShirtIcon';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../constants/album-folder-tags';
import AlbumFolderPurchaseUniformTagModalPhotos from './AlbumFolderPurchaseUniformTagModalPhotos';
import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../../global/Alert';
import {
  isAlbumFolderTagSelectionRequiredForCheckout,
  allowsPartialSmartTagCompletion,
} from '../../../util/album-folder';
import noop from '../../../util/noop';
import { sortArrayByKey } from '../../../util/sorting';
import { getIdentityName } from '../../../util/identity';
import { photoUploadFilter } from '../../../util/photo-upload';

class AlbumFolderPurchaseUniformTagModal extends Component {
  static propTypes = {
    actionProps: PropTypes.shape({
      tag: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.oneOf(Object.keys(ALBUM_FOLDER_TAG_TYPE)),
        uniform_enabled: PropTypes.bool,
        uniform_numbered: PropTypes.bool,
        uniform_different_home_and_away: PropTypes.bool,
        levels: PropTypes.shape({
          records: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          ),
        }),
      }).isRequired,
      identity: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }).isRequired,
      metadata: PropTypes.shape({
        jerseyNumber: PropTypes.string,
        levelId: PropTypes.string,
        homeJersey: PropTypes.object,
        awayJersey: PropTypes.object,
      }),
      subscriptionTag: PropTypes.shape({
        id: PropTypes.string,
        album_folder_tag: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
      onSuccess: PropTypes.func,
    }),
    closeModal: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    selectTag: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    identityAlbumFolderTagUpdate: PropTypes.func,
    updatedTag: PropTypes.func,
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);

    const { actionProps } = this.props;
    const { metadata } = actionProps;

    this.state = {
      jerseyNumber: metadata?.jerseyNumber ?? null,
      levelId: metadata?.levelId ?? '',
      teamId: metadata?.teamId ?? '',
      homeJersey: metadata?.homeJersey ?? null,
      awayJersey: metadata?.awayJersey ?? null,
      updating: false,
      error: '',
    };
  }

  onFileInputChange = async event => {
    const { name } = event.target;
    const [file] = event.target.files;

    if (!photoUploadFilter(file)) {
      return;
    }

    this.setState(() => ({ [name]: file }));
  };

  onInputChange = event => {
    const { name } = event.target;
    const { value } = event.target;

    this.setState(() => ({ [name]: value }));
  };

  onSubmit = async event => {
    event.preventDefault();

    if (!this.isFormValid()) {
      return;
    }

    const {
      selectTag,
      closeModal,
      actionProps: { tag, identity, subscriptionTag, onSuccess = noop },
      identityAlbumFolderTagUpdate,
      updatedTag,
    } = this.props;

    const { levelId, jerseyNumber, homeJersey, awayJersey, teamId, updating } =
      this.state;

    if (updating) {
      return;
    }

    const identityMetadata = {
      levelId: levelId || undefined,
      jerseyNumber,
      homeJersey: homeJersey || undefined,
      awayJersey: awayJersey || undefined,
      teamId: teamId || undefined,
    };

    if (subscriptionTag) {
      // this is actually an update:
      this.setState(() => ({
        updating: true,
        error: '',
      }));

      try {
        await identityAlbumFolderTagUpdate(
          subscriptionTag,
          identityMetadata,
          identity,
        );
        updatedTag();
        closeModal(UNIFORM_TAG_FORM_MODAL);
        onSuccess(identity);
      } catch (error) {
        this.setState(() => ({
          error: getErrorString(error),
          updating: false,
        }));
      }
    } else {
      selectTag(identity, {
        ...tag,
        identityMetadata,
      });

      closeModal(UNIFORM_TAG_FORM_MODAL);
      onSuccess(identity);
    }
  };

  isFormValid = () => {
    const { albumFolder } = this.props;

    if (allowsPartialSmartTagCompletion(albumFolder)) {
      return true;
    }

    return this.isFormComplete();
  };

  isFormComplete = () => {
    const {
      actionProps: { tag },
      albumFolder,
    } = this.props;

    const { levelId, homeJersey, awayJersey, jerseyNumber } = this.state;

    const uniformEnabled = tag?.uniform_enabled;
    const uniformPhotoUpload = false && uniformEnabled; // At the moment, this will be disabled
    const differentHomeAway = tag?.uniform_different_home_and_away;

    if (
      uniformEnabled &&
      differentHomeAway &&
      uniformPhotoUpload &&
      (!homeJersey || !awayJersey)
    ) {
      return false;
    }

    if ((tag?.levels?.records ?? []).length > 0) {
      return !!levelId;
    }

    const requiresUniformNumber = uniformEnabled && tag?.uniform_numbered;

    if (
      requiresUniformNumber &&
      isAlbumFolderTagSelectionRequiredForCheckout(albumFolder) &&
      !jerseyNumber
    ) {
      return false;
    }

    return true;
  };

  render() {
    const {
      jerseyNumber,
      levelId,
      teamId,
      awayJersey,
      homeJersey,
      updating,
      error,
    } = this.state;
    const {
      actionProps: { tag, identity },
      openModal,
      onRequestClose,
      albumFolder,
    } = this.props;

    const allowsPartialSmartTagCompletionBool =
      allowsPartialSmartTagCompletion(albumFolder);
    const uniformEnabled = tag?.uniform_enabled;
    const allowsUniformNumber = uniformEnabled && tag?.uniform_numbered;
    const levels = sortArrayByKey(tag?.levels?.records ?? [], 'name');
    const requiresJersey =
      uniformEnabled &&
      allowsUniformNumber &&
      isAlbumFolderTagSelectionRequiredForCheckout(albumFolder) &&
      !allowsPartialSmartTagCompletionBool;
    const teams =
      levels.find(level => level?.id === levelId)?.teams?.records ?? [];
    const selectedTeam = teams.find(team => teamId === team.id);

    return (
      <form
        onSubmit={this.onSubmit}
        className="d-flex flex-column justify-content-center"
      >
        <div className="text-center mb-2">
          <img
            src={getAsset('/waldo-sports.png')}
            alt="Waldo logo"
            width="90"
            className="mb-2"
          />

          {allowsUniformNumber ? (
            <h3>Look who&rsquo;s got jersey recognition!</h3>
          ) : (
            <h3>Is it hot in here?</h3>
          )}

          {requiresJersey ? (
            <p>
              Please provide your team and jersey number below and I&rsquo;ll
              find and deliver your photos even when I can&rsquo;t see your
              face.
            </p>
          ) : (
            <p>
              I’m sweatin’! It’s a lot easier for me to find and deliver photos
              if you provide a bit more information.
            </p>
          )}
        </div>

        {levels.length > 0 && (
          <Select
            id="level"
            name="levelId"
            label={`Select from the choices below for ${getIdentityName(
              identity,
            )}`}
            labelNote={!allowsPartialSmartTagCompletionBool ? '(required)' : ''}
            onChange={this.onInputChange}
            value={levelId}
            required={!allowsPartialSmartTagCompletionBool}
            disabled={updating}
            className="mb-2"
          >
            <option value="">
              Select {getIdentityName(identity)}&rsquo;s level&hellip;
            </option>
            {levels.map(level => (
              <option key={level.id} value={level.id}>
                {level.name}
              </option>
            ))}
          </Select>
        )}

        {teams.length > 0 && (
          <Fragment>
            <Select
              id="team-tag-select"
              name="teamId"
              label={`Select a team for ${getIdentityName(identity)}`}
              onChange={this.onInputChange}
              value={teamId}
              className="mb-2"
            >
              <option value="">
                {allowsPartialSmartTagCompletionBool
                  ? 'Not sure yet'
                  : 'Press to select...'}
              </option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>

            {
              // In case Coach name is ever optional don't show it if
              // we can't display it.
              (!teamId || !!selectedTeam.coach_name) && (
                <Select
                  id="coach-tag-select"
                  name="teamId"
                  label="Coach name"
                  onChange={this.onInputChange}
                  value={teamId}
                  className="mb-2"
                >
                  <option value="">
                    {allowsPartialSmartTagCompletionBool
                      ? 'Not sure yet'
                      : 'Press to select...'}
                  </option>
                  {teams
                    .filter(team => !!team.coach_name)
                    .map(team => (
                      <option key={team.id} value={team.id}>
                        {team.coach_name}
                      </option>
                    ))}
                </Select>
              )
            }
          </Fragment>
        )}

        {allowsUniformNumber && (
          <InputGroup
            id="uniform-number"
            type="text"
            label={`Enter ${identity.name}'s Jersey Number ${
              allowsPartialSmartTagCompletionBool
                ? '(Leave empty if unsure)'
                : ''
            }`}
            name="jerseyNumber"
            defaultValue={jerseyNumber}
            onChange={this.onInputChange}
            addon={<ShirtIcon size="20px" color="#cdd6e4" />}
            disabled={updating}
            required={requiresJersey}
            labelNote={requiresJersey ? '(required)' : ''}
          />
        )}

        <AlbumFolderPurchaseUniformTagModalPhotos
          onFileInputChange={this.onFileInputChange}
          tag={tag}
          openModal={openModal}
          awayJersey={awayJersey}
          homeJersey={homeJersey}
        />

        {error && <Alert type="danger">{error}</Alert>}

        {!this.isFormComplete() && this.isFormValid() && (
          <Alert type="info">
            Please fill out as much as you can for better matches. Any
            information you don&rsquo;t know, can be updated later.
          </Alert>
        )}

        <Button
          type="submit"
          disabled={!this.isFormValid() || updating}
          className="mt-1"
        >
          {!updating ? 'Submit' : 'Loading...'}
        </Button>

        <Button
          onClick={onRequestClose}
          className="mt-2"
          disabled={updating}
          btnStyle="link"
        >
          Cancel
        </Button>
      </form>
    );
  }
}

export default withModal(AlbumFolderPurchaseUniformTagModal, {
  name: UNIFORM_TAG_FORM_MODAL,
  hideHeader: true,
  size: 'sm',
  type: 'full',
});
